import ADND from '@aytm/lib/adnd/ADND'
import React, { memo, useCallback, useMemo, useRef } from 'react'

import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import usePool from '@duckness/use-pool'

import DragBar from './DragBar'
import Pin from './Pin'

import { useDashboard, useGroupedHover } from '@aytm/app/insights/dashboard/components/hooks'
import { useBlinkSubscription } from '@aytm/app/insights/dashboard/components/hooks/useBlink'
import type { ItemType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { compareTuples } from '@aytm/app/insights/dashboard/lib/comparison'

import './Benchmark.scss'

const CLASS = 'insights-portal-benchmark'

type DragData = {
  index: number
  uid?: string | null
}

type BenchmarkProps = {
  className?: string
  item?: ItemType
  index?: number
  draggable?: boolean
  dragItemIndex?: number
  controls?: string[]
  editor?: { type: 'replace' | 'edit' | 'change' | 'rename' | 'add' }
  onPin?: (payload: { pinned: boolean; index: number; uid: string | null }) => void
  onReplace?: (payload: { from: number; to: number }) => void
  onDrag?: (payload: { action: string; index: number; uid?: string | null; data?: DragData }) => void
  onEdit?: (payload: { index: number; uid: string | null }) => void
}

const BenchmarkInternal: React.FC<BenchmarkProps> = ({
  className: propsClassName,
  index: propsIndex,
  dragItemIndex: propsDragItemIndex,
  item: propsItem,
  editor: propsEditor,
  controls: propsControls,
  onPin,
  onEdit,
  draggable = false,
  onDrag,
  onReplace
}) => {
  const className = propsClassName || CLASS

  const { editorMode, isSharedView } = useDashboard()

  const request = usePool(
    InsightsDashboardPool,
    (state: InsightsDashboardPoolState) => state.currentData?.request || null
  )

  const index = propsIndex ?? -1
  const dragItemIndex = propsDragItemIndex ?? -1
  const isDragging = index === dragItemIndex
  const isPinned = propsItem?.pinned || false
  const uid = propsItem?.uid || null

  const editing = propsEditor && 'add' !== propsEditor?.type

  const controls = useMemo(() => propsControls || [], [propsControls])

  const targetRef = useBlinkSubscription(uid)

  const containerClassName = isDragging ? `${className} ${className}-drag` : className

  const handlePin = (event: { pinned: boolean }) => {
    if (onPin) {
      onPin({ pinned: event.pinned, index, uid })
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit({ index, uid })
    }
  }

  const pin =
    (isPinned || controls.includes('pin')) && !editing ? (
      <Pin className={`${className}-pin`} pinned={isPinned} onPin={handlePin} />
    ) : null

  const edit =
    editorMode && controls.includes('edit') && !request ? (
      <i className={`icon-aytmfd-pencil  ${className}-edit`} onClick={handleEdit} />
    ) : (
      <div className={`${className}-edit`}></div>
    )

  const text = isPinned ? <div className={`${className}-text`}>Benchmark</div> : null

  const dragBar =
    draggable && !isSharedView && !editing ? (
      <DragBar className={`${className}-drag-bar`} index={index} uid={uid} onDrag={onDrag} />
    ) : null

  const onDragRef =
    useRef<(payload: { action: string; index: number; uid?: string | null; data?: DragData }) => void>(null)
  onDragRef.current = onDrag

  const onReplaceRef = useRef<(payload: { from: number; to: number }) => void>(null)
  onReplaceRef.current = onReplace

  const dragSource = useRef<{ connect: (node: HTMLElement | null) => void }>(null)
  if (!dragSource.current) {
    dragSource.current = ADND.targets
      .data({
        acceptTypes: ['concept']
      })
      .onEnter(() => {
        onDragRef.current?.({ action: 'enter', index, uid })
      })
      .onLeave(() => {
        onDragRef.current?.({ action: 'leave', index, uid })
      })
      .onDrop(event => {
        const data = event.data as DragData
        onDragRef.current?.({ action: 'drop', index, uid, data })
      })
  }

  const [ref] = useGroupedHover(index, 'benchmark')

  const onElementMount = useCallback(
    (node: HTMLElement | null) => {
      ref(node)
      targetRef.current = node
      dragSource.current?.connect?.(node)
    },
    [ref, targetRef]
  )

  return (
    <div ref={onElementMount} className={containerClassName}>
      <div className={`${className}-left`}>
        {pin}
        {edit}
        {text}
      </div>
      <div className={`${className}-right`}>{dragBar}</div>
    </div>
  )
}

const arePropsEqual = compareTuples<BenchmarkProps>(({ item, index, draggable, dragItemIndex, controls } = {}) => [
  item,
  index,
  draggable,
  dragItemIndex,
  controls
])

const Benchmark = memo(BenchmarkInternal, arePropsEqual)

Benchmark.displayName = 'Benchmark'

export default Benchmark
